import { BasicInput } from '@/view/components/BasicInput'
import { Button } from '@/view/components/ui/button'
import { FormProvider, useForm } from 'react-hook-form'
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/view/components/ui/dialog'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { useQuery } from '@tanstack/react-query'
import { useMemo, useRef } from 'react'
import { useToast } from '@/view/components/ui/use-toast'
import { Edit } from 'lucide-react'
import { banks } from '@/app/services/transactionService/banks'
import { editTransaction } from '@/app/services/transactionService/edit'
import { Spinner } from '@/view/components/ui/spinner'
import { BasicSelect } from '@/view/components/BasicSelect'
import BasicOptions from '@/view/components/BasicOptions'
import { accountType } from '@/app/utils/accountType'
import masks from '@/app/utils/masks'
import { useTransactionId } from '@/app/hooks/useTransactionId'
import { Transaction } from '@/app/entities/Transaction'

const bankValidationSchema = z
  .object({
    accountType: z.string().min(1, 'Campo obrigatório'),
    bank: z.string().min(1, 'Campo obrigatório'),
    agency: z.string().min(1, 'Campo obrigatório'),
    account: z.string().min(1, 'Campo obrigatório'),
  })
  .required()

const contactValidationSchema = z
  .object({
    phone: z.string().min(11, 'Número inválido'),
    email: z.string().email('E-mail inválido').min(1, 'Campo obrigatório'),
  })
  .required()

type FormData = z.infer<
  typeof bankValidationSchema & typeof contactValidationSchema
>

interface ModalEditTransactionProps {
  selectedTransaction: Transaction
}

export const ModalEditTransaction = ({
  selectedTransaction,
}: ModalEditTransactionProps) => {
  const modalCloseRef = useRef<HTMLButtonElement>(null)

  const { toast } = useToast()

  const { data: transaction } = useTransactionId(selectedTransaction._id)

  const handleSubmit = async (formData: any) => {
    try {
      await editTransaction({ transactionId: transaction?._id, ...formData })

      toast({ title: 'Transação atualizada com sucesso' })
    } catch (error: any) {
      toast({ title: 'Não foi possível atualizar a transação' })
    } finally {
      modalCloseRef.current?.click()
    }
  }

  const { data: bankOptions } = useQuery({
    queryKey: ['banks', {}],
    queryFn: async () => await banks(),
    select(data) {
      return (
        data.banks.map(
          ({ value, label }: { value: string; label: string }) => ({
            label: `${value} - ${label}`,
            value,
          }),
        ) || []
      )
    },
  })

  const showContactOnly = useMemo(() => {
    return !!transaction?.config?.canEditContact
  }, [transaction])

  const validationSchema = useMemo(() => {
    return showContactOnly ? contactValidationSchema : bankValidationSchema
  }, [showContactOnly])

  const form = useForm<FormData>({
    resolver: zodResolver(validationSchema),
  })

  return (
    <Dialog>
      <DialogClose ref={modalCloseRef} />
      {transaction?.config?.canEdit && (
        <DialogTrigger>
          <div className="flex items-center justify-between hover:underline">
            <Edit width={24} />
          </div>
        </DialogTrigger>
      )}

      <DialogContent className="sm:max-w-md">
        <form onSubmit={form.handleSubmit(handleSubmit, (e) => console.log(e))}>
          <FormProvider {...form}>
            <DialogHeader className="border-b pb-4">
              <DialogTitle className="text-xl font-semibold text-black01">
                Editar transação
              </DialogTitle>
            </DialogHeader>

            <DialogDescription className="flex flex-col gap-4 py-4 text-base text-black04">
              {showContactOnly ? (
                <>
                  <BasicInput
                    name="phone"
                    label="Número de telefone"
                    error={form.formState.errors.phone?.message}
                    onChange={(e) =>
                      (e.target.value = masks.cell(e.target.value))
                    }
                  />
                  <BasicInput
                    name="email"
                    label="E-mail"
                    error={form.formState.errors.email?.message}
                  />
                </>
              ) : (
                <>
                  <div className="flex flex-col gap-2">
                    <span>Tipo de conta</span>
                    <BasicOptions
                      name="accountType"
                      options={accountType}
                      error={form.formState.errors.accountType?.message}
                    />
                  </div>
                  {bankOptions && (
                    <BasicSelect
                      name="bank"
                      options={bankOptions}
                      label="Banco"
                      placeholder="Selecione o banco"
                      error={form.formState.errors.bank?.message}
                    />
                  )}
                  <BasicInput
                    name="agency"
                    label="Agência"
                    placeholder="0000"
                    error={form.formState.errors.agency?.message}
                    onChange={(e) =>
                      (e.target.value = masks.number(e.target.value))
                    }
                  />
                  <BasicInput
                    name="account"
                    label="Conta c/ digito"
                    placeholder="0000000"
                    error={form.formState.errors.account?.message}
                    onChange={(e) =>
                      (e.target.value = masks.number(e.target.value))
                    }
                  />
                </>
              )}
            </DialogDescription>

            <DialogFooter>
              <DialogClose>
                <Button className="bg-gray02 text-primary hover:bg-gray01">
                  Cancelar
                </Button>
              </DialogClose>
              <Button
                type="submit"
                disabled={form.formState.isSubmitting}
                className="text-white"
              >
                {form.formState.isSubmitting ? (
                  <Spinner className="text-white" />
                ) : (
                  'Confirmar'
                )}
              </Button>
            </DialogFooter>
          </FormProvider>
        </form>
      </DialogContent>
    </Dialog>
  )
}
